import { useCallback, useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminChat() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const r = await api.get("/chat/threads");
      setThreads(r.data || []);
      if (r.data?.length) setActive((cur) => cur || r.data[0].thread_id);
    } catch (e) {
      console.warn("Failed to load chat threads:", e);
    }
  }, []);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/chat/threads/${id}/messages`);
      setMessages(r.data || []);
    } catch (e) {
      console.warn("Failed to load chat messages:", e);
    }
  }, []);

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 5000);
    return () => clearInterval(t);
  }, [loadThreads]);

  useEffect(() => {
    if (active) {
      loadMessages(active);
      api.post(`/chat/threads/${active}/mark-read`).catch(() => {});
    }
  }, [active, loadMessages]);

  useEffect(() => {
    if (active) {
      const t = setInterval(() => loadMessages(active), 4000);
      return () => clearInterval(t);
    }
  }, [active, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post(`/chat/threads/${active}/reply`, { text: text.trim() });
      setText("");
      loadMessages(active);
      loadThreads();
    } catch (err) {
      toast.error("Could not send");
    }
  };

  return (
    <div data-testid="admin-chat">
      <h2 className="font-serif text-3xl text-primary">Live Chat</h2>
      <p className="text-sm text-muted-foreground">Real-time conversations with site visitors.</p>

      <div className="mt-6 grid md:grid-cols-12 gap-4 border border-soft bg-card min-h-[480px]">
        <aside className="md:col-span-4 lg:col-span-3 border-r border-soft overflow-y-auto" data-testid="thread-list">
          {threads.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No conversations yet.</div>
          ) : (
            threads.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => setActive(t.thread_id)}
                data-testid={`thread-${t.thread_id}`}
                className={cn(
                  "w-full text-left px-4 py-3 border-b border-soft transition-colors",
                  active === t.thread_id ? "bg-secondary" : "hover:bg-secondary/50"
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-primary text-sm truncate">{t.last_name || "Guest"}</span>
                  {t.unread > 0 && (
                    <span className="ml-2 h-5 min-w-[20px] inline-flex items-center justify-center rounded-full bg-accent text-accent-foreground text-[10px] px-1">
                      {t.unread}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground truncate">
                  {t.last_sender === "admin" && <span className="text-accent">You: </span>}
                  {t.last_message}
                </p>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {new Date(t.last_at).toLocaleString()}
                </p>
              </button>
            ))
          )}
        </aside>

        <section className="md:col-span-8 lg:col-span-9 flex flex-col min-h-[480px]">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a conversation
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="thread-messages">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn("flex", m.sender === "admin" ? "justify-end" : "justify-start")}
                  >
                    <div
                      className={cn(
                        "max-w-[70%] px-4 py-2 rounded-sm text-sm",
                        m.sender === "admin" ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"
                      )}
                    >
                      <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">
                        {m.sender === "admin" ? "You" : m.name}
                      </div>
                      <div>{m.text}</div>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center pt-12">No messages yet.</p>
                )}
              </div>
              <form onSubmit={send} className="border-t border-soft p-3 flex gap-2" data-testid="admin-reply-form">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a reply…"
                  data-testid="admin-reply-input"
                  className="flex-1"
                />
                <Button type="submit" data-testid="admin-reply-send" className="bg-accent text-accent-foreground hover:bg-accent/90">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
